/* eslint-disable no-unused-vars */
// import React, { useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  Button,
  Dialog,
} from "@mui/material";
import { useState, useEffect } from "react";

// BPInput component for blood pressure input fields
function AutoFormattedBPInput({ value, onChange }) {
  const [systolic = "", diastolic = ""] = value.split("/");

  const handleSystolicChange = (e) => {
    const newSys = e.target.value.replace(/\D/g, "").slice(0, 3);
    onChange(`${newSys}/${diastolic}`);
  };

  const handleDiastolicChange = (e) => {
    const newDia = e.target.value.replace(/\D/g, "").slice(0, 3);
    onChange(`${systolic}/${newDia}`);
  };

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        border: "1px solid #0F9BB0",
        borderRadius: "6px",
        overflow: "hidden",
        fontFamily: "Poppins",
        fontSize: "14px",
      }}
    >
      <input
        type="text"
        value={systolic}
        onChange={handleSystolicChange}
        placeholder="__"
        inputMode="numeric"
        maxLength={3}
        style={{
          width: "36px",
          height: "30px",
          padding: "4px 6px",
          border: "none",
          textAlign: "center",
        }}
      />
      <span
        style={{
          padding: "0 4px",
          userSelect: "none",
        }}
      >
        /
      </span>
      <input
        type="text"
        value={diastolic}
        onChange={handleDiastolicChange}
        placeholder="__"
        inputMode="numeric"
        maxLength={3}
        style={{
          width: "36px",
          height: "30px",
          padding: "4px 6px",
          border: "none",
          textAlign: "center",
        }}
      />
    </div>
  );
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// function WalkTestChart({ formData }) {
function WalkTestChart({
  formData,
  selectedValues,
  setSelectedValues,
  supplementalOxygen,
  dyspneaValues,
  setDyspneaValues,
  fatigueValues,
  setFatigueValues,
  bpValues,
  setBpValues,
}) {
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text:
          supplementalOxygen === "" ||
          parseInt(supplementalOxygen) === 0 ||
          supplementalOxygen === "N/A"
            ? `6-Minute Walk and Recovery`
            : `6-Minute Walk and Recovery on Supplemental O2`,
        font: {
          family: "Poppins",
          size: 14,
          weight: "500",
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Time (Minutes)",
          font: {
            family: "Poppins",
          },
        },
      },
      y: {
        type: "linear",
        display: true,
        position: "left",
        min: 40,
        max: 200,
        title: {
          display: true,
          text: "Pulse Rate (bpm)",
          font: {
            family: "Poppins",
          },
        },
      },
      y1: {
        type: "linear",
        display: true,
        position: "right",
        min: 70,
        max: 100,
        title: {
          display: true,
          text: "SpO₂ (%)",
          font: {
            family: "Poppins",
          },
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  const [initialGreyCells, setInitialGreyCells] = useState({});
  const [initialInvalidCells, setInitialInvalidCells] = useState({});

  // Add touched state for validation indicators - expand to include SpO₂ and PR fields
  const [touchedFields, setTouchedFields] = useState({
    dyspnea_rest: false,
    dyspnea_max: false,
    fatigue_rest: false,
    fatigue_max: false,
    // Add SpO₂ touched states
    spo2_rest: false,
    spo2_walk_1: false,
    spo2_walk_2: false,
    spo2_walk_3: false,
    spo2_walk_4: false,
    spo2_walk_5: false,
    spo2_walk_6: false,
    spo2_recovery_1: false,
    spo2_recovery_2: false,
    spo2_recovery_3: false,
    // Add PR touched states
    pr_rest: false,
    pr_walk_1: false,
    pr_walk_2: false,
    pr_walk_3: false,
    pr_walk_4: false,
    pr_walk_5: false,
    pr_walk_6: false,
    pr_recovery_1: false,
    pr_recovery_2: false,
    pr_recovery_3: false,
  });

  // Handler to update bpValues state
  const handleBPChange = (key, value) => {
    setBpValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [selectedKey, setSelectedKey] = useState("");

  // Dropdown for Dyspnea and Fatigue scores (0-10)
  function renderScoreDropdown(type, key, value) {
    const handleChange = (event) => {
      const newValue = event.target.value;
      if (type === "dyspnea") {
        setDyspneaValues((prev) => ({ ...prev, [key]: newValue }));
        setTouchedFields((prev) => ({ ...prev, [`dyspnea_${key}`]: true }));
      } else if (type === "fatigue") {
        setFatigueValues((prev) => ({ ...prev, [key]: newValue }));
        setTouchedFields((prev) => ({ ...prev, [`fatigue_${key}`]: true }));
      }
    };

    const handleBlur = () => {
      if (type === "dyspnea") {
        setTouchedFields((prev) => ({ ...prev, [`dyspnea_${key}`]: true }));
      } else if (type === "fatigue") {
        setTouchedFields((prev) => ({ ...prev, [`fatigue_${key}`]: true }));
      }
    };

    const isRequired = key === "rest" || key === "max";
    const fieldKey = `${type}_${key}`;
    const showError = touchedFields[fieldKey] && isRequired && !value;

    return (
      <div>
        <Select
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          size="small"
          displayEmpty
          style={{
            width: "90px",
            height: "32px",
            border: showError ? "1px solid #d32f2f" : "none",
          }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
                width: "50px",
              },
            },
          }}
        >
          <MenuItem value="">
            <em>-</em>
          </MenuItem>
          {[...Array(11).keys()].map((score) => (
            <MenuItem
              key={score}
              value={score.toString()}
              style={{
                justifyContent: "center",
                padding: "4px",
              }}
            >
              {score}
            </MenuItem>
          ))}
        </Select>
        {showError && (
          <div
            style={{
              color: "#d32f2f",
              fontSize: "12px",
              marginTop: "4px",
              fontFamily: "Poppins",
              position: "absolute",
            }}
          >
            Required
          </div>
        )}
      </div>
    );
  }

  // Set up initial grey cells on component mount
  useEffect(() => {
    const greyMap = {};
    // Check each minute's data for initial grey state
    Object.entries(formData || {}).forEach(([key, data]) => {
      if (Array.isArray(data)) {
        const selectedItem = data.find((item) => item.is_selected);
        if (selectedItem && selectedItem.is_grey) {
          const minuteKey = key.replace("_min", "");
          greyMap[minuteKey] = true;
        }
      }
    });
    setInitialGreyCells(greyMap);

    // Check each minute's data for initial invalid state
    const invalidMap = {};

    Object.entries(formData || {}).forEach(([key, data]) => {
      if (Array.isArray(data)) {
        const allSpo2Invalid = data.every((item) => item.spo2 === "N/A");

        if (allSpo2Invalid) {
          const minuteKey = key.replace("_min", ""); // Adjust key format if needed
          invalidMap[minuteKey] = true;
        }
      }
    });
    setInitialInvalidCells(invalidMap);
  }, [formData]);

  // Handle dropdown selection change
  const handleTimeChange = (minute, selectedItem) => {
    setSelectedValues((prev) => ({
      ...prev,
      [minute]: selectedItem,
    }));
  };

  // Update chart data based on selected values
  const chartData = {
    labels: [
      "Rest",
      "1min",
      "2min",
      "3min",
      "4min",
      "5min",
      "6min",
      "R1",
      "R2",
      "R3",
    ],
    datasets: [
      {
        label: "Pulse",
        data: [
          selectedValues.rest?.pr || null,
          selectedValues.walk_1?.pr || null,
          selectedValues.walk_2?.pr || null,
          selectedValues.walk_3?.pr || null,
          selectedValues.walk_4?.pr || null,
          selectedValues.walk_5?.pr || null,
          selectedValues.walk_6?.pr || null,
          selectedValues.recovery_1?.pr || null,
          selectedValues.recovery_2?.pr || null,
          selectedValues.recovery_3?.pr || null,
        ],
        borderColor: "#0000FF",
        borderDash: [5, 5],
        tension: 0.4,
        yAxisID: "y",
      },
      {
        label: "SpO₂",
        data: [
          selectedValues.rest?.spo2 || null,
          selectedValues.walk_1?.spo2 || null,
          selectedValues.walk_2?.spo2 || null,
          selectedValues.walk_3?.spo2 || null,
          selectedValues.walk_4?.spo2 || null,
          selectedValues.walk_5?.spo2 || null,
          selectedValues.walk_6?.spo2 || null,
          selectedValues.recovery_1?.spo2 || null,
          selectedValues.recovery_2?.spo2 || null,
          selectedValues.recovery_3?.spo2 || null,
        ],
        borderColor: "#00FF00",
        borderDash: [5, 5],
        tension: 0.4,
        yAxisID: "y1",
      },
    ],
  };

  // Helper function to render PR cell values with validation
  function renderPrCell(key, value) {
    // Check if we have a selected value for this key
    if (!selectedValues[key]) return "-";

    // If PR value is "N/A" or was previously converted to input, show input field
    if (initialInvalidCells[key]) {
      const handleInputChange = (e) => {
        const newValue = e.target.value;
        // Allow positive numbers or empty string only
        if (
          newValue === "" ||
          (/^\d*\.?\d*$/.test(newValue) && parseFloat(newValue) >= 0)
        ) {
          const updatedItem = {
            ...selectedValues[key],
            pr: newValue === "" ? "N/A" : parseInt(newValue),
            prConvertedToInput: true,
          };
          setSelectedValues((prev) => ({
            ...prev,
            [key]: updatedItem,
          }));
        }
        // Mark as touched when changed
        setTouchedFields((prev) => ({
          ...prev,
          [`pr_${key}`]: true,
        }));
      };

      const handleBlur = () => {
        // Mark as touched when field loses focus
        setTouchedFields((prev) => ({
          ...prev,
          [`pr_${key}`]: true,
        }));
      };

      // Check if field is touched and empty
      const showError =
        touchedFields[`pr_${key}`] &&
        (value === "N/A" || value === "-" || value === "");

      return (
        <input
          type="number"
          min="0"
          placeholder="-"
          value={value === "N/A" || value === "-" ? "" : value}
          onChange={handleInputChange}
          onBlur={handleBlur}
          style={{
            width: "90px",
            height: "32px",
            padding: "4px 8px",
            border: showError ? "1px solid #d32f2f" : "1px solid #0F9BB0",
            borderRadius: "4px",
            fontFamily: "Poppins",
            fontSize: "14px",
            textAlign: "center",
          }}
        />
      );
    }

    return value;
  }

  // Update tableData.rows to use the renderPrCell function
  const tableData = {
    headers: [
      "",
      "Resting",
      "1min",
      "2min",
      "3min",
      "4min",
      "5min",
      "6min",
      "Max",
    ],
    subHeaders: ["Min1", "Min2", "Min3"],
    rows: [
      {
        label: "PR bpm",
        values: [
          renderPrCell("rest", selectedValues.rest?.pr || "-"),
          renderPrCell("walk_1", selectedValues.walk_1?.pr || "-"),
          renderPrCell("walk_2", selectedValues.walk_2?.pr || "-"),
          renderPrCell("walk_3", selectedValues.walk_3?.pr || "-"),
          renderPrCell("walk_4", selectedValues.walk_4?.pr || "-"),
          renderPrCell("walk_5", selectedValues.walk_5?.pr || "-"),
          renderPrCell("walk_6", selectedValues.walk_6?.pr || "-"),
          getMaxPrInWalks(selectedValues) || "-",
        ],
        recovery: [
          renderPrCell("recovery_1", selectedValues.recovery_1?.pr || "-"),
          renderPrCell("recovery_2", selectedValues.recovery_2?.pr || "-"),
          renderPrCell("recovery_3", selectedValues.recovery_3?.pr || "-"),
        ],
      },
      {
        label: (
          <span>
            BP (mmHg)
            <span style={{ color: "#666666", fontSize: "12px" }}>
              {" "}
              (Optional)
            </span>
          </span>
        ),
        values: [
          <AutoFormattedBPInput
            key="rest"
            value={bpValues.rest}
            onChange={(value) => handleBPChange("rest", value)}
          />,
          "-",
          "-",
          "-",
          "-",
          "-",
          "-",
          <AutoFormattedBPInput
            key="max"
            value={bpValues.max}
            onChange={(value) => handleBPChange("max", value)}
          />,
        ],
        recovery: [
          <AutoFormattedBPInput
            key="recovery_1"
            value={bpValues.recovery_1}
            onChange={(value) => handleBPChange("recovery_1", value)}
          />,
          <AutoFormattedBPInput
            key="recovery_2"
            value={bpValues.recovery_2}
            onChange={(value) => handleBPChange("recovery_2", value)}
          />,
          <AutoFormattedBPInput
            key="recovery_3"
            value={bpValues.recovery_3}
            onChange={(value) => handleBPChange("recovery_3", value)}
          />,
        ],
      },
      {
        label: (
          <span>
            Dyspnea (0-10)
            <span style={{ color: "#d32f2f", marginLeft: "2px" }}>*</span>
          </span>
        ),
        values: [
          renderScoreDropdown("dyspnea", "rest", dyspneaValues.rest),
          "-",
          "-",
          "-",
          "-",
          "-",
          "-",
          renderScoreDropdown("dyspnea", "max", dyspneaValues.max),
        ],
        recovery: [
          dyspneaValues.recovery_1 || "-",
          dyspneaValues.recovery_2 || "-",
          dyspneaValues.recovery_3 || "-",
        ],
      },
      {
        label: (
          <span>
            Fatigue (0-10)
            <span style={{ color: "#d32f2f", marginLeft: "2px" }}>*</span>
          </span>
        ),
        values: [
          renderScoreDropdown("fatigue", "rest", fatigueValues.rest),
          "-",
          "-",
          "-",
          "-",
          "-",
          "-",
          renderScoreDropdown("fatigue", "max", fatigueValues.max),
        ],
        recovery: [
          // Just show the values instead of dropdowns
          fatigueValues.recovery_1 || "-",
          fatigueValues.recovery_2 || "-",
          fatigueValues.recovery_3 || "-",
        ],
      },
    ],
  };

  // Generate menu items for a specific minute's data
  const generateMenuItems = (minuteData) => {
    if (!minuteData || !Array.isArray(minuteData)) return [];

    return [
      <MenuItem
        key="header"
        disabled
        style={{
          fontFamily: "Poppins",
          fontSize: "14px",
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr", // Equal width columns
          gap: "16px",
          padding: "8px 16px",
          width: "300px",
          borderBottom: "1px solid #E0E0E0",
          backgroundColor: "#F5F5F5",
        }}
      >
        <span style={{ fontWeight: 500 }}>Time</span>
        <span style={{ fontWeight: 500 }}>SpO₂</span>
        <span style={{ fontWeight: 500 }}>PR</span>
      </MenuItem>,
      ...minuteData.map((item, index) => (
        <MenuItem
          key={index}
          value={item}
          style={{
            fontFamily: "Poppins",
            fontSize: "14px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr", // Equal width columns
            gap: "16px",
            padding: "8px 16px",
            width: "300px",
          }}
        >
          <span style={{ color: "#666666" }}>{item.time}</span>
          <span
            style={{
              color: !item?.is_grey
                ? item.spo2 < 90
                  ? "#E51414" // red
                  : item.spo2 <= 94
                  ? "#F7B500" // orange/yellow
                  : "#197F8F" // greenish-blue
                : "#0000004D", // greyed out
            }}
          >
            {item.spo2}%
          </span>
          <span style={{ color: "#666666" }}>{item.pr} bpm</span>
        </MenuItem>
      )),
    ];
  };

  // Render cell content - modified to handle N/A values with input fields permanently and add validation
  const renderCell = (minuteData, key) => {
    if (!minuteData || !Array.isArray(minuteData)) return "-";

    const selectedItem = selectedValues[key];

    if (!selectedItem) return "-";
    if (selectedItem.is_grey && initialInvalidCells[key]) {
      const handleInputChange = (e) => {
        const value = e.target.value;
        if (value === "" || (parseInt(value) > 0 && parseInt(value) <= 100)) {
          const updatedItem = {
            ...selectedItem,
            spo2: value === "" ? "N/A" : parseInt(value),
            convertedToInput: true,
          };
          setSelectedValues((prev) => ({
            ...prev,
            [key]: updatedItem,
          }));
        }
        // Mark as touched when changed
        setTouchedFields((prev) => ({
          ...prev,
          [`spo2_${key}`]: true,
        }));
      };

      const handleBlur = () => {
        // Mark as touched when field loses focus
        setTouchedFields((prev) => ({
          ...prev,
          [`spo2_${key}`]: true,
        }));
      };

      // Check if field is touched and empty
      const showError =
        touchedFields[`spo2_${key}`] &&
        (selectedItem.spo2 === "N/A" || selectedItem.spo2 === "");

      return (
        <input
          type="number"
          min="0"
          max="100"
          placeholder="-"
          value={selectedItem.spo2 === "N/A" ? "" : selectedItem.spo2}
          onChange={handleInputChange}
          onBlur={handleBlur}
          style={{
            width: "90px",
            height: "32px",
            padding: "4px 8px",
            border: showError ? "1px solid #d32f2f" : "1px solid #0F9BB0",
            borderRadius: "4px",
            fontFamily: "Poppins",
            fontSize: "14px",
            textAlign: "center",
          }}
        />
      );
    }

    // Check if the minute needs dropdown (if it was initially grey)
    if (selectedItem.is_grey || initialGreyCells[key]) {
      return (
        <Select
          value={selectedItem}
          onChange={(e) => handleTimeChange(key, e.target.value)}
          size="small"
          style={{ width: "90px", height: "32px" }}
          renderValue={(value) => `${value.spo2}%`}
        >
          {generateMenuItems(minuteData)}
        </Select>
      );
    }

    return selectedItem.spo2;
  };

  function getMaxSpo2InWalks(data) {
    let maxSpo2 = null;

    for (let i = 1; i <= 6; i++) {
      const key = `walk_${i}`;
      const spo2 = data[key]?.spo2;

      if (spo2 !== "N/A" && spo2 !== undefined && spo2 !== null) {
        const parsedSpo2 = parseInt(spo2, 10);
        if (!isNaN(parsedSpo2)) {
          maxSpo2 =
            maxSpo2 === null ? parsedSpo2 : Math.min(maxSpo2, parsedSpo2);
        }
      }
    }

    return maxSpo2;
  }

  // Update the getMaxPrInWalks function to handle any numeric values
  function getMaxPrInWalks(data) {
    let maxPr = null;

    for (let i = 1; i <= 6; i++) {
      const key = `walk_${i}`;
      const pr = data[key]?.pr;

      if (pr !== "N/A" && pr !== undefined && pr !== null && pr !== "-") {
        const parsedPr = parseFloat(pr);
        if (!isNaN(parsedPr)) {
          maxPr = maxPr === null ? parsedPr : Math.max(maxPr, parsedPr);
        }
      }
    }

    return maxPr;
  }

  // Remove modal and score selector components
  return (
    <div>
      <div style={{ height: "300px", marginBottom: "24px" }}>
        <Line options={options} data={chartData} />
      </div>

      <TableContainer
        component={Paper}
        variant="outlined"
        style={{ marginBottom: "24px" }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              {tableData.headers.map((header, index) => (
                <TableCell
                  key={index}
                  align={index === 0 ? "left" : "center"}
                  style={{
                    fontFamily: "Poppins",
                    fontSize: "14px",
                    color: "#444444",
                    fontWeight: 500,
                    borderBottom: "1px solid #E0E0E0",
                  }}
                >
                  {header}
                </TableCell>
              ))}
              <TableCell
                colSpan={3}
                align="center"
                style={{
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  color: "#444444",
                  fontWeight: 500,
                  borderBottom: "1px solid #E0E0E0",
                }}
              >
                Recovery
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                style={{ borderBottom: "1px solid #E0E0E0" }}
              ></TableCell>
              {tableData.headers.slice(1).map((_, index) => (
                <TableCell
                  key={index}
                  align="center"
                  style={{ borderBottom: "1px solid #E0E0E0" }}
                ></TableCell>
              ))}
              {tableData.subHeaders.map((header, index) => (
                <TableCell
                  key={index}
                  align="center"
                  style={{
                    fontFamily: "Poppins",
                    fontSize: "14px",
                    color: "#444444",
                    borderBottom: "1px solid #E0E0E0",
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* SpO₂ Row */}
            <TableRow>
              <TableCell
                style={{
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  color: "#444444",
                }}
              >
                SpO₂ (%)
              </TableCell>
              <TableCell align="center">
                {renderCell(formData?.rest_min, "rest")}
              </TableCell>
              <TableCell align="center">
                {renderCell(formData?.walk_min_1, "walk_1")}
              </TableCell>
              <TableCell align="center">
                {renderCell(formData?.walk_min_2, "walk_2")}
              </TableCell>
              <TableCell align="center">
                {renderCell(formData?.walk_min_3, "walk_3")}
              </TableCell>
              <TableCell align="center">
                {renderCell(formData?.walk_min_4, "walk_4")}
              </TableCell>
              <TableCell align="center">
                {renderCell(formData?.walk_min_5, "walk_5")}
              </TableCell>
              <TableCell align="center">
                {renderCell(formData?.walk_min_6, "walk_6")}
              </TableCell>
              <TableCell align="center">
                {getMaxSpo2InWalks(selectedValues) || "N/A"}
              </TableCell>
              {/* Removed Max column */}
              <TableCell align="center">
                {renderCell(formData?.recovery_min_1, "recovery_1")}
              </TableCell>
              <TableCell align="center">
                {renderCell(formData?.recovery_min_2, "recovery_2")}
              </TableCell>
              <TableCell align="center">
                {renderCell(formData?.recovery_min_3, "recovery_3")}
              </TableCell>
            </TableRow>
            {/* PR Row - Updates automatically from selectedValues */}
            {tableData.rows.map((row, index) => (
              <TableRow key={index}>
                <TableCell
                  style={{
                    fontFamily: "Poppins",
                    fontSize: "14px",
                    color: "#444444",
                  }}
                >
                  {row.label}
                </TableCell>
                {row.values.map((value, vIndex) => (
                  <TableCell
                    key={vIndex}
                    align="center"
                    style={{
                      fontFamily: "Poppins",
                      fontSize: "14px",
                      color: "#444444",
                    }}
                  >
                    {value}
                  </TableCell>
                ))}
                {row.recovery.map((value, rIndex) => (
                  <TableCell
                    key={rIndex}
                    align="center"
                    style={{
                      fontFamily: "Poppins",
                      fontSize: "14px",
                      color: "#444444",
                    }}
                  >
                    {value}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

export default WalkTestChart;
